import z from "zod/v4";
import createError from "http-errors";
import { TenantId, SiteId, AssetCameraKey } from "@visioni/a3s-schema";
import { TenantIdCodec, SiteIdCodec, AssetCameraKeySchema } from "@visioni/a3s-schema/codecs";

// Re-export resource and endpoint definitions
export { defineResource, type ResourceConfig, type AnyResourceConfig, type ResourceScope } from "./resource";
export {
  defineEndpoint,
  expandEndpoint,
  type EndpointConfig,
  type AnyEndpointConfig,
  type GetEndpointConfig,
  type MutationEndpointConfig,
  type HttpMethod,
} from "./endpoint";

// ==================== Wildcard ====================

/**
 * Wildcard placeholder used in URL path segments to mean "any value".
 *
 * Per ADR 0031, callers can substitute `WILDCARD` for `siteId` or `assetCameraKey`
 * to fan out a request across all sites or all cameras for a tenant in a single
 * round-trip — for example `/sites/-/cameras/-/snapshots`.
 */
export const WILDCARD = "-" as const;
export type Wildcard = typeof WILDCARD;

/**
 * Codec mapping the wire-level wildcard literal `"-"` to `undefined` in memory.
 *
 * Decoding a wildcard yields `undefined` so feature handlers can use a single
 * "missing or wildcard" branch; encoding `undefined` produces `WILDCARD` so
 * URL building never emits empty path segments.
 */
export const Wildcard = z.codec(z.literal(WILDCARD), z.undefined(), {
  decode: () => undefined,
  encode: () => WILDCARD,
});

/** Type guard for the wildcard literal. */
export function isWildcard(value: unknown): value is Wildcard {
  return value === WILDCARD;
}

// ==================== Subject Types ====================

/**
 * Camera subject schema for API responses.
 * Identifies the site and camera for a resource.
 */
export const CameraSubject = z.object({
  site: SiteIdCodec,
  camera: AssetCameraKeySchema,
});

export type CameraSubject = z.output<typeof CameraSubject>;

// ==================== Response Schema Factories ====================

type ItemSchemaWithSubject = z.ZodObject<{ subject: typeof CameraSubject }>;

/**
 * Creates a collection response schema for a list of items.
 * Standard format: { data: T[], cursor?: string }
 * Item schema must include a `subject: CameraSubject` field.
 */
export function CollectionResponse<T extends ItemSchemaWithSubject>(itemSchema: T) {
  return z.object({
    data: z.array(itemSchema),
    cursor: z.string().optional(),
  });
}

/**
 * Creates a single response schema for a single item.
 * Standard format: { data: T }
 * Item schema must include a `subject: CameraSubject` field.
 */
export function SingleResponse<T extends ItemSchemaWithSubject>(itemSchema: T) {
  return z.object({
    data: itemSchema,
  });
}

// ==================== Path Utilities ====================

/** A concrete site id, or the wildcard `"-"` meaning "any site". */
export const SiteIdOrWildcard = z.union([SiteIdCodec, Wildcard]);
export type SiteIdOrWildcard = z.infer<typeof SiteIdOrWildcard>;

/** A concrete asset-camera key, or the wildcard `"-"` meaning "any camera". */
export const AssetCameraKeyOrWildcard = z.union([AssetCameraKeySchema, Wildcard]);
export type AssetCameraKeyOrWildcard = z.infer<typeof AssetCameraKeyOrWildcard>;

/**
 * Site/camera path segments for a camera-scoped feature route.
 *
 * The discriminating constraint is intentional: if you provide a concrete
 * `siteId`, you may also provide a concrete or wildcard `assetCameraKey`;
 * if you omit `siteId` (treated as wildcard) then `assetCameraKey` must
 * also be wildcard or omitted. This prevents nonsensical URLs like
 * `/sites/-/cameras/<concrete>/...` which would scope to a specific
 * camera under an unspecified site.
 */
export const ClientApiFeatureSubPathParams = z.union([
  z.object({
    siteId: SiteIdCodec,
    assetCameraKey: AssetCameraKeyOrWildcard.optional(),
  }),
  z.object({
    siteId: SiteIdOrWildcard.optional(),
    assetCameraKey: Wildcard.optional(),
  }),
]);
export type ClientApiFeatureSubPathParams = z.output<typeof ClientApiFeatureSubPathParams>;

/**
 * Full camera-scoped path params: `tenantId` plus the optional site/camera
 * segments. This is the schema used to encode/decode the path portion of
 * camera-scoped client API URLs.
 */
export const ClientApiFeaturePathParams = ClientApiFeatureSubPathParams.and(z.object({ tenantId: TenantIdCodec }));
export type ClientApiFeaturePathParams = z.output<typeof ClientApiFeaturePathParams>;

/**
 * Simple object schema for base path params, without union/wildcard complexity.
 * Use this when you need .partial() for encoding partial path params.
 */
export const BasePathParamsSchema = z.object({
  tenantId: TenantIdCodec,
  siteId: SiteIdCodec,
  assetCameraKey: AssetCameraKeySchema,
});

// ==================== Tenant-Only Path Params ====================

/**
 * Path params for tenant-scoped resources (no site/camera).
 */
export const TenantPathParams = z.object({ tenantId: TenantIdCodec });
export type TenantPathParams = z.output<typeof TenantPathParams>;

// ==================== Path Building ====================

/**
 * Builds the path for a camera-scoped client API endpoint.
 * Single source of truth for camera-scoped path structure.
 */
export function buildClientApiPath(
  feature: string,
  resource: string,
  params: { tenantId: string; siteId?: string | undefined; assetCameraKey?: string | undefined },
): string {
  // Note: apiBaseUrl from realm.api already includes /api prefix
  const segments = [
    "v2",
    "tenants",
    params.tenantId,
    "features",
    feature,
    "sites",
    params.siteId ?? WILDCARD,
    "cameras",
    params.assetCameraKey ?? WILDCARD,
    resource,
  ];
  return "/" + segments.join("/");
}

/**
 * Builds the path for a tenant-scoped client API endpoint.
 * No site/camera segments - for features that don't operate on cameras.
 */
export function buildFeaturePath(feature: string, resource: string, params: { tenantId: string }): string {
  const segments = ["v2", "tenants", params.tenantId, "features", feature, resource];
  return "/" + segments.join("/");
}

// ==================== Wildcard Validation ====================

/**
 * Path parameters with concrete (non-wildcard) site and camera values.
 */
export interface ConcretePathParams {
  tenantId: TenantId;
  siteId: SiteId;
  assetCameraKey: AssetCameraKey;
}

/**
 * Assert that path parameters do not contain wildcards.
 * Throws a BadRequest error if wildcards are present.
 * Narrows the type to ConcretePathParams for type-safe access.
 */
export function assertNoWildcards(pathParams: ClientApiFeaturePathParams): asserts pathParams is ConcretePathParams {
  if (!pathParams.siteId || !pathParams.assetCameraKey) {
    throw createError.BadRequest("Wildcards are not supported for this endpoint");
  }
}
